"use client";

import React, { useEffect, useMemo, useState } from "react";
import { ComponentConfig } from "@/types/models/componentConfig";
import { ProductSelector } from "@/components/common/ProductSelector";
import { CollectionSelector } from "./CollectionSelector";
import { CategorySelector } from "./CategorySelector";
import { MetafieldSelector } from "./MetafieldSelector";
import { ColorPicker } from "./ColorPicker";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, ChevronDown, Monitor, Tablet, Smartphone } from "lucide-react";
import { useImagePicker } from "@/hooks/useFilePicker";
import { useAppSelector } from "@/store/hooks";
import { Page } from "@/types/models/page";

type ObjectRecord = Record<string, unknown>;
type ViewportMode = "desktop" | "tablet" | "mobile";

interface StructuredValueEditorProps {
  type: ComponentConfig["type"];
  value: unknown;
  onChange: (value: unknown) => void;
}

const isPlainObject = (value: unknown): value is ObjectRecord =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const isResponsiveScalarObject = (value: unknown): value is Record<ViewportMode, unknown> => {
  if (!isPlainObject(value)) return false;
  const keys = Object.keys(value);
  return (
    keys.length > 0 &&
    keys.every((key) => key === "desktop" || key === "tablet" || key === "mobile") &&
    keys.some((key) => key === "desktop") &&
    Object.values(value).every((entry) => !isPlainObject(entry) && !Array.isArray(entry))
  );
};

const deepClone = <T,>(value: T): T => {
  if (value === null || typeof value !== "object") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => deepClone(item)) as T;
  }

  return Object.entries(value as Record<string, unknown>).reduce(
    (acc, [key, entry]) => {
      (acc as Record<string, unknown>)[key] = deepClone(entry);
      return acc;
    },
    {} as Record<string, unknown>,
  ) as T;
};

const toTitle = (key: string): string =>
  key
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const commonFontSizes = [10, 12, 14, 16, 18, 20, 24, 28, 32, 40, 48, 56, 64];
const commonFontWeights = [
  "100",
  "200",
  "300",
  "400",
  "500",
  "600",
  "700",
  "800",
  "900",
  "normal",
  "medium",
  "semibold",
  "bold",
];
const commonTextAlignValues = ["left", "center", "right", "justify"];
const commonImageFitValues = ["cover", "contain", "fill", "none", "scale-down"];
const commonPositionValues = [
  "center",
  "top",
  "bottom",
  "left",
  "right",
  "top left",
  "top center",
  "top right",
  "center left",
  "center right",
  "bottom left",
  "bottom center",
  "bottom right",
];
const commonTextTransformValues = ["none", "uppercase", "lowercase", "capitalize"];
const boxShadowPresetValues: Record<string, string> = {
  none: "none",
  sm: "0 1px 2px rgba(0, 0, 0, 0.08)",
  md: "0 4px 10px rgba(0, 0, 0, 0.12)",
  lg: "0 10px 15px rgba(0, 0, 0, 0.14)",
  xl: "0 20px 25px rgba(0, 0, 0, 0.18)",
};

type BoxShadowLayer = {
  inset: boolean;
  x: number;
  y: number;
  blur: number;
  spread: number;
  color: string;
};

const defaultBoxShadowLayer = (): BoxShadowLayer => ({
  inset: false,
  x: 0,
  y: 10,
  blur: 15,
  spread: 0,
  color: "rgba(0, 0, 0, 0.14)",
});

const normalizeBoxShadowValue = (value: unknown): string => {
  if (typeof value !== "string") {
    return "";
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return "";
  }

  return boxShadowPresetValues[trimmed] || trimmed;
};

const isBoxShadowLength = (token: string): boolean =>
  /^-?(?:\d+|\d*\.\d+)(?:px|r?em|rem|%|vw|vh|vmin|vmax|pt|pc|ch|ex|cm|mm|in)?$/i.test(token.trim());

const parseBoxShadowLength = (token: string): number => {
  const parsed = Number.parseFloat(token);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatBoxShadowLength = (value: number): string =>
  `${Number.isFinite(value) ? value : 0}px`;

const splitBoxShadowLayers = (value: string): string[] => {
  const layers: string[] = [];
  let current = "";
  let depth = 0;

  for (const char of value) {
    if (char === "(") {
      depth += 1;
    } else if (char === ")") {
      depth = Math.max(0, depth - 1);
    }

    if (char === "," && depth === 0) {
      const trimmed = current.trim();
      if (trimmed) {
        layers.push(trimmed);
      }
      current = "";
      continue;
    }

    current += char;
  }

  const trimmed = current.trim();
  if (trimmed) {
    layers.push(trimmed);
  }

  return layers;
};

const tokenizeBoxShadowLayer = (value: string): string[] => {
  const tokens: string[] = [];
  let current = "";
  let depth = 0;

  for (const char of value) {
    if (char === "(") {
      depth += 1;
    } else if (char === ")") {
      depth = Math.max(0, depth - 1);
    }

    if (/\s/.test(char) && depth === 0) {
      if (current.trim()) {
        tokens.push(current.trim());
      }
      current = "";
      continue;
    }

    current += char;
  }

  if (current.trim()) {
    tokens.push(current.trim());
  }

  return tokens;
};

const parseBoxShadowLayer = (value: string): BoxShadowLayer | null => {
  const normalized = normalizeBoxShadowValue(value);

  if (!normalized || normalized === "none") {
    return null;
  }

  const tokens = tokenizeBoxShadowLayer(normalized);
  if (!tokens.length) {
    return null;
  }

  const lengthTokens: string[] = [];
  const colorTokens: string[] = [];
  let inset = false;

  tokens.forEach((token) => {
    const normalizedToken = token.toLowerCase();

    if (normalizedToken === "inset") {
      inset = true;
      return;
    }

    if (normalizedToken === "outset") {
      return;
    }

    if (isBoxShadowLength(token)) {
      lengthTokens.push(token);
      return;
    }

    colorTokens.push(token);
  });

  return {
    inset,
    x: parseBoxShadowLength(lengthTokens[0] || "0"),
    y: parseBoxShadowLength(lengthTokens[1] || "0"),
    blur: parseBoxShadowLength(lengthTokens[2] || "0"),
    spread: parseBoxShadowLength(lengthTokens[3] || "0"),
    color: colorTokens.join(" ").trim() || "rgba(0, 0, 0, 0.14)",
  };
};

const parseBoxShadowValue = (value: unknown): BoxShadowLayer[] => {
  const normalized = normalizeBoxShadowValue(value);

  if (!normalized || normalized === "none") {
    return [];
  }

  return splitBoxShadowLayers(normalized)
    .map((layer) => parseBoxShadowLayer(layer))
    .filter((layer): layer is BoxShadowLayer => Boolean(layer));
};

const serializeBoxShadowLayer = (layer: BoxShadowLayer): string => {
  const parts = [
    ...(layer.inset ? ["inset"] : []),
    formatBoxShadowLength(layer.x),
    formatBoxShadowLength(layer.y),
    formatBoxShadowLength(layer.blur),
    formatBoxShadowLength(layer.spread),
  ];

  if (layer.color.trim()) {
    parts.push(layer.color.trim());
  }

  return parts.join(" ");
};

const serializeBoxShadowValue = (layers: BoxShadowLayer[]): string =>
  layers.length ? layers.map((layer) => serializeBoxShadowLayer(layer)).join(", ") : "none";

const getScalarSelectOptions = (
  key: string,
): { options: Array<{ label: string; value: string }>; parseAsNumber?: boolean } => {
  const normalizedKey = key.toLowerCase();

  if (normalizedKey === "fontsize") {
    return {
      options: commonFontSizes.map((size) => ({
        label: `${size}px`,
        value: String(size),
      })),
      parseAsNumber: true,
    };
  }

  if (normalizedKey === "fontweight") {
    return {
      options: commonFontWeights.map((weight) => ({
        label: weight,
        value: weight,
      })),
    };
  }

  if (normalizedKey === "textalign") {
    return {
      options: commonTextAlignValues.map((value) => ({
        label: toTitle(value),
        value,
      })),
    };
  }

  if (
    normalizedKey === "fit" ||
    normalizedKey === "objectfit" ||
    normalizedKey === "backgroundsize"
  ) {
    return {
      options: commonImageFitValues.map((value) => ({
        label: toTitle(value),
        value,
      })),
    };
  }

  if (
    normalizedKey === "position" ||
    normalizedKey === "backgroundposition" ||
    normalizedKey === "objectposition"
  ) {
    return {
      options: commonPositionValues.map((value) => ({
        label: toTitle(value),
        value,
      })),
    };
  }

  if (normalizedKey === "texttransform") {
    return {
      options: commonTextTransformValues.map((value) => ({
        label: toTitle(value),
        value,
      })),
    };
  }

  return { options: [] };
};

const createStableId = (prefix: string): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
};

const defaultMenuItem = () => ({
  id: createStableId("menu"),
  label: "",
  url: "",
  type: "link",
  target: "_self",
  icon: "",
  badge: { text: "", color: "neutral" },
  children: [] as unknown[],
});

const defaultSocialLink = () => ({
  platform: "custom",
  url: "",
  icon: "",
  label: "",
});

const defaultTextListItem = () => ({
  text: "",
  description: "",
  url: "",
  target: "_self",
});

const defaultLinkListItem = () => ({
  label: "",
  url: "",
  target: "_self",
  icon: "",
});

const defaultImageListItem = () => ({
  assetId: "",
  alt: "",
  caption: "",
  link: "",
  title: "",
});

const defaultIconListItem = () => ({
  icon: "",
  label: "",
  description: "",
  url: "",
  target: "_self",
});

const defaultFeatureCardItem = () => ({
  title: "",
  description: "",
  icon: "",
  assetId: "",
  link: "",
  target: "_self",
  badge: "",
});

const defaultTestimonialItem = () => ({
  quote: "",
  author: "",
  role: "",
  avatarId: "",
  rating: 5,
});

const defaultFaqItem = () => ({
  question: "",
  answer: "",
});

const defaultAccordionItem = () => ({
  title: "",
  content: "",
  open: false,
});

const defaultTabItem = () => ({
  label: "",
  content: "",
  icon: "",
  value: "",
});

const defaultMediaGalleryItem = () => ({
  mediaType: "image",
  assetId: "",
  url: "",
  posterId: "",
  caption: "",
  alt: "",
  title: "",
});

const defaultPromoCardItem = (index: number = 0) => {
  const presets = [
    { assetId: "", title: "Vomero 18", price: "", ctaText: "Shop", href: "", alt: "Vomero 18" },
    { assetId: "", title: "V2K", price: "", ctaText: "Shop", href: "", alt: "V2K" },
    { assetId: "", title: "Pegasus 41", price: "", ctaText: "Shop", href: "", alt: "Pegasus 41" },
    { assetId: "", title: "Vomero 5", price: "", ctaText: "Shop", href: "", alt: "Vomero 5" },
  ];

  return {
    ...presets[index % presets.length],
    backgroundSize: "contain",
    backgroundPosition: "center",
    minHeight: 300,
    borderRadius: 8,
  };
};

const defaultSectionStyleGroup = () => ({
  enabled: true,
  fontSize: 48,
  fontWeight: "bold",
  lineHeight: 1.1,
  textAlign: "left",
  color: "#000000",
  marginBottom: 48,
});

const defaultCardImageStyleGroup = () => ({
  enabled: true,
  fit: "contain",
  position: "center",
  borderRadius: 8,
  minHeight: 300,
});

const defaultCardTitleStyleGroup = () => ({
  enabled: true,
  fontSize: 20,
  fontWeight: "bold",
  lineHeight: 1.2,
  textAlign: "left",
  color: "#FFFFFF",
  marginBottom: 16,
});

const defaultCardCtaStyleGroup = () => ({
  enabled: true,
  backgroundColor: "#FFFFFF",
  textColor: "#000000",
  borderRadius: 50,
  paddingX: 24,
  paddingY: 8,
  fontWeight: "medium",
});

const defaultCardStyleGroup = () => ({
  enabled: true,
  width: 288,
  height: undefined as number | undefined,
  minHeight: 300,
  maxWidth: 320,
  borderRadius: 8,
  shadow: "0 10px 15px rgba(0, 0, 0, 0.14)",
  borderWidth: 0,
  padding: 0,
  overflow: "hidden",
});

const defaultStructuredValue = (type: ComponentConfig["type"]): unknown => {
  switch (type) {
    case "product-selector":
      return {
        enabled: true,
        mode: "filter",
        filter: {
          tags: [],
          collections: [],
          priceRange: { min: undefined, max: undefined },
          status: "active",
          availability: "in_stock",
          sortBy: "created_at",
          sortOrder: "desc",
          limit: 10,
          offset: 0,
        },
      };
    case "product-filter":
      return {
        tags: [],
        collections: [],
        priceRange: { min: undefined, max: undefined },
        status: "active",
        availability: "in_stock",
        sortBy: "created_at",
        sortOrder: "desc",
        limit: 10,
        offset: 0,
      };
    case "category-selector":
      return {
        mode: "all",
        categoryIds: [],
        filter: {
          level: 1,
          minProducts: 0,
          maxCategories: 10,
          sortBy: "position",
          sortOrder: "asc",
          excludeEmpty: true,
        },
      };
    case "category-filter":
      return {
        level: 1,
        minProducts: 0,
        maxCategories: 10,
        sortBy: "position",
        sortOrder: "asc",
        excludeEmpty: true,
      };
    case "collection-selector":
      return {
        mode: "filter",
        collectionIds: [],
        filter: {
          featured: true,
          status: "active",
          sortBy: "created_at",
          sortOrder: "desc",
          limit: 6,
        },
      };
    case "product-metafield-binding":
      return {
        appliedTo: "products",
        metafieldKey: "",
        metafieldType: "text",
      };
    case "collection-metafield-binding":
      return {
        appliedTo: "collections",
        metafieldKey: "",
        metafieldType: "text",
      };
    case "asset-selector":
      return {
        type: "image",
        multiple: false,
        assetIds: [],
        maxCount: 1,
        aspectRatio: "16:9",
      };
    case "navigation-menu":
      return { items: [], layout: "horizontal", maxDepth: 2 };
    case "text-list":
    case "link-list":
    case "image-list":
    case "icon-list":
    case "feature-card-list":
    case "testimonial-list":
    case "faq-list":
    case "accordion-list":
    case "tab-list":
    case "media-gallery":
      return [];
    case "social-links-config":
      return { links: [], display: "icons", size: "medium", openInNewTab: true };
    case "spacing-config":
      return {
        marginTop: 0,
        marginBottom: 0,
        marginLeft: 0,
        marginRight: 0,
        paddingTop: 16,
        paddingBottom: 16,
        paddingLeft: 0,
        paddingRight: 0,
        unit: "px",
      };
    case "typography-config":
      return {
        fontFamily: "",
        fontSize: 16,
        fontWeight: "normal",
        lineHeight: 1.5,
        letterSpacing: 0,
        textTransform: "none",
        textAlign: "left",
        color: "#000000",
      };
    case "grid-config":
      return {
        columns: { desktop: 4, tablet: 2, mobile: 1 },
        gap: { desktop: 24, tablet: 16, mobile: 12 },
        aspectRatio: "",
        itemsPerRow: { desktop: 4, tablet: 2, mobile: 1 },
      };
    case "animation-config":
      return {
        enabled: false,
        type: "fade",
        duration: 300,
        delay: 0,
        easing: "ease-in-out",
        trigger: "scroll",
      };
    case "swiper-config":
      return {
        slidesPerView: {
          desktop: 4,
          tablet: 2,
          mobile: 1.1,
        },
        spaceBetween: {
          desktop: 24,
          tablet: 20,
          mobile: 16,
        },
        slidesPerGroup: 1,
        effect: "slide",
        speed: 550,
        loop: false,
        rewind: false,
        autoplay: {
          enabled: false,
          delay: 3000,
          disableOnInteraction: true,
          pauseOnMouseEnter: true,
        },
        centeredSlides: false,
        centeredSlidesBounds: false,
        grabCursor: true,
        allowTouchMove: true,
        simulateTouch: true,
        watchOverflow: true,
        freeMode: false,
      };
    case "seo-config":
      return {
        title: "",
        description: "",
        keywords: [],
        ogTitle: "",
        ogDescription: "",
        ogImage: "",
        twitterCard: "summary_large_image",
        canonicalUrl: "",
        noindex: false,
        nofollow: false,
      };
    case "style-config":
      return {
        enabled: true,
        sectionHeading: {
          fontSize: 48,
          fontWeight: "bold",
          lineHeight: 1.1,
          textAlign: "left",
          color: "#000000",
          marginBottom: 48,
        },
        cardHeading: {
          fontSize: 20,
          fontWeight: "bold",
          lineHeight: 1.2,
          textAlign: "left",
          color: "#FFFFFF",
          marginBottom: 16,
        },
        text: {
          fontSize: 16,
          fontWeight: "normal",
          lineHeight: 1.5,
          textAlign: "left",
          color: "#111111",
        },
        link: {
          color: "#111111",
          hoverColor: "#000000",
          underline: false,
        },
        image: {
          fit: "contain",
          position: "center",
          borderRadius: 8,
          minHeight: 300,
        },
        card: {
          backgroundColor: "#000000",
          borderColor: "#000000",
          borderRadius: 8,
          shadow: "lg",
          padding: 24,
        },
        cta: {
          backgroundColor: "#FFFFFF",
          textColor: "#000000",
          borderRadius: 50,
          paddingX: 24,
          paddingY: 8,
          fontWeight: "medium",
        },
      };
    case "section-style-config":
      return defaultSectionStyleGroup();
    case "card-image-style-config":
      return defaultCardImageStyleGroup();
    case "card-title-style-config":
      return defaultCardTitleStyleGroup();
    case "card-cta-style-config":
      return defaultCardCtaStyleGroup();
    case "card-style-config":
      return defaultCardStyleGroup();
    case "promo-card-list":
      return [
        defaultPromoCardItem(0),
        defaultPromoCardItem(1),
        defaultPromoCardItem(2),
        defaultPromoCardItem(3),
      ];
    default:
      return {};
  }
};

const normalizeStructuredValue = (
  type: ComponentConfig["type"],
  rawValue: unknown,
): unknown => {
  if (rawValue === undefined || rawValue === null || rawValue === "") {
    return deepClone(defaultStructuredValue(type));
  }

  switch (type) {
    case "product-selector": {
      const defaults = deepClone(defaultStructuredValue(type)) as ObjectRecord;
      if (Array.isArray(rawValue)) {
        return {
          ...defaults,
          mode: "specific",
          productIds: rawValue,
        };
      }
      return {
        ...defaults,
        ...((isPlainObject(rawValue) ? rawValue : {}) as ObjectRecord),
      };
    }
    case "collection-selector": {
      const defaults = deepClone(defaultStructuredValue(type)) as ObjectRecord;
      if (Array.isArray(rawValue)) {
        return {
          ...defaults,
          mode: "specific",
          collectionIds: rawValue,
        };
      }
      return {
        ...defaults,
        ...((isPlainObject(rawValue) ? rawValue : {}) as ObjectRecord),
      };
    }
    case "category-selector": {
      const defaults = deepClone(defaultStructuredValue(type)) as ObjectRecord;
      if (Array.isArray(rawValue)) {
        return {
          ...defaults,
          mode: "specific",
          categoryIds: rawValue,
        };
      }
      return {
        ...defaults,
        ...((isPlainObject(rawValue) ? rawValue : {}) as ObjectRecord),
      };
    }
    case "product-metafield-binding":
    case "collection-metafield-binding": {
      const defaults = deepClone(defaultStructuredValue(type)) as ObjectRecord;
      if (typeof rawValue === "string") {
        return {
          ...defaults,
          metafieldKey: rawValue,
        };
      }
      return {
        ...defaults,
        ...((isPlainObject(rawValue) ? rawValue : {}) as ObjectRecord),
      };
    }
    case "asset-selector": {
      const defaults = deepClone(defaultStructuredValue(type)) as ObjectRecord;
      if (Array.isArray(rawValue)) {
        return {
          ...defaults,
          assetIds: rawValue,
          multiple: true,
          maxCount: rawValue.length,
        };
      }
      return {
        ...defaults,
        ...((isPlainObject(rawValue) ? rawValue : {}) as ObjectRecord),
      };
    }
    case "text-list":
    case "link-list":
    case "image-list":
    case "icon-list":
    case "feature-card-list":
    case "testimonial-list":
    case "faq-list":
    case "accordion-list":
    case "tab-list":
    case "media-gallery":
    case "promo-card-list":
      return Array.isArray(rawValue) ? deepClone(rawValue) : [];
    default:
      if (Array.isArray(rawValue) || isPlainObject(rawValue)) {
        return deepClone(rawValue);
      }
      return rawValue;
  }
};

const getArrayItemDefault = (
  type: ComponentConfig["type"],
  path: string,
  currentArray: unknown[],
): unknown => {
  if (currentArray.length > 0) {
    const first = currentArray[0];
    if (Array.isArray(first)) {
      return [];
    }
    if (isPlainObject(first)) {
      return {};
    }
    return "";
  }

  const normalizedPath = path.endsWith(".children")
    ? "children"
    : path.endsWith(".links")
      ? "links"
      : path.endsWith(".items")
        ? "items"
        : path.endsWith(".tags")
          ? "tags"
          : path.endsWith(".collections")
            ? "collections"
            : path;

  switch (`${type}:${normalizedPath}`) {
    case "navigation-menu:items":
    case "navigation-menu:children":
      return defaultMenuItem();
    case "social-links-config:links":
      return defaultSocialLink();
    case "text-list":
      return defaultTextListItem();
    case "link-list":
      return defaultLinkListItem();
    case "image-list":
      return defaultImageListItem();
    case "icon-list":
      return defaultIconListItem();
    case "feature-card-list":
      return defaultFeatureCardItem();
    case "testimonial-list":
      return defaultTestimonialItem();
    case "faq-list":
      return defaultFaqItem();
    case "accordion-list":
      return defaultAccordionItem();
    case "tab-list":
      return defaultTabItem();
    case "media-gallery":
      return defaultMediaGalleryItem();
    case "promo-card-list":
      return defaultPromoCardItem(currentArray.length);
    case "product-selector:tags":
    case "product-selector:collections":
    case "collection-selector:collectionIds":
    case "asset-selector:assetIds":
    case "seo-config:keywords":
      return "";
    default:
      return "";
  }
};

const renderScalarEditor = (
  key: string,
  value: unknown,
  onChange: (value: unknown) => void,
  options?: {
    forceTextarea?: boolean;
    forceUrl?: boolean;
    forceColor?: boolean;
    forceNumber?: boolean;
    forceSelect?: boolean;
    selectOptions?: Array<{ label: string; value: string }>;
    selectValueType?: "string" | "number";
    numberMin?: number;
    numberMax?: number;
  },
) => {
  if (typeof value === "boolean") {
    return (
      <div className="flex items-center gap-2">
        <Checkbox
          checked={value}
          onCheckedChange={(checked) => onChange(Boolean(checked))}
        />
        <span className="text-xs text-muted-foreground">
          {key ? toTitle(key) : "Enabled"}
        </span>
      </div>
    );
  }

  const scalarSelect = getScalarSelectOptions(key);
  const selectOptions =
    options?.selectOptions?.length ? options.selectOptions : scalarSelect.options;
  const selectValueType =
    options?.selectValueType ?? (scalarSelect.parseAsNumber ? "number" : "string");

  if (options?.forceSelect || selectOptions.length > 0) {
    return (
      <Select
        value={typeof value === "string" ? value : typeof value === "number" ? String(value) : ""}
        onValueChange={(next) =>
          onChange(selectValueType === "number" ? Number(next) : next)
        }
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Select option" />
        </SelectTrigger>
        <SelectContent>
          {selectOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (options?.forceNumber || typeof value === "number") {
    return (
      <Input
        type="number"
        value={typeof value === "number" && Number.isFinite(value) ? value : ""}
        onChange={(e) =>
          onChange(e.target.value === "" ? undefined : Number(e.target.value))
        }
        min={options?.numberMin}
        max={options?.numberMax}
      />
    );
  }

  const stringValue = typeof value === "string" ? value : "";

  if (options?.forceColor) {
    return <ColorPicker color={stringValue || "#000000"} onChange={onChange} />;
  }

  if (options?.forceSelect && options.selectOptions?.length) {
    return (
      <Select
        value={stringValue}
        onValueChange={(next) => onChange(next)}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Select option" />
        </SelectTrigger>
        <SelectContent>
          {options.selectOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (options?.forceTextarea || stringValue.length > 80 || stringValue.includes("\n")) {
    return (
      <Textarea
        value={stringValue}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
      />
    );
  }

  return (
    <Input
      type={options?.forceUrl ? "url" : "text"}
      value={stringValue}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

const responsiveViewportOptions: Array<{ value: ViewportMode; label: string; Icon: typeof Monitor }> = [
  { value: "desktop", label: "Desktop", Icon: Monitor },
  { value: "tablet", label: "Tablet", Icon: Tablet },
  { value: "mobile", label: "Mobile", Icon: Smartphone },
];

const ResponsiveScalarObjectField = ({
  label,
  fieldKey,
  value,
  onChange,
  scalarOptions,
}: {
  label: string;
  fieldKey: string;
  value: Record<ViewportMode, unknown>;
  onChange: (value: unknown) => void;
  scalarOptions?: Parameters<typeof renderScalarEditor>[3];
}) => {
  const [viewport, setViewport] = useState<ViewportMode>("desktop");
  const currentValue = value[viewport] ?? value.desktop;

  return (
    <div className="space-y-2 border-b border-border py-3 last:border-b-0">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-xs font-medium text-muted-foreground">{label}</div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{viewport}</div>
        </div>
        <div className="flex items-center rounded-md border border-border bg-background p-0.5">
          {responsiveViewportOptions.map(({ value: optionValue, label: optionLabel, Icon }) => (
            <button
              key={optionValue}
              type="button"
              title={optionLabel}
              aria-label={optionLabel}
              className={`rounded px-1.5 py-1 transition-colors ${
                viewport === optionValue
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
              onClick={() => setViewport(optionValue)}
            >
              <Icon className="h-3.5 w-3.5" />
            </button>
          ))}
        </div>
      </div>
      {renderScalarEditor(fieldKey, currentValue, (next) => {
        onChange({
          ...value,
          [viewport]: next,
        });
      }, scalarOptions)}
    </div>
  );
};

const BoxShadowEditor: React.FC<{
  value: unknown;
  onChange: (value: unknown) => void;
}> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = React.useRef<HTMLDivElement>(null);

  const layers = useMemo(() => parseBoxShadowValue(value), [value]);
  const currentSummary = layers.length
    ? `${layers.length} layer${layers.length === 1 ? "" : "s"}`
    : "No shadow";
  const currentValue = layers.length ? serializeBoxShadowValue(layers) : "none";

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const updateLayers = (nextLayers: BoxShadowLayer[]) => {
    onChange(serializeBoxShadowValue(nextLayers));
  };

  const updateLayer = (
    index: number,
    patch: Partial<BoxShadowLayer>,
  ) => {
    updateLayers(
      layers.map((layer, layerIndex) =>
        layerIndex === index ? { ...layer, ...patch } : layer,
      ),
    );
  };

  return (
    <div ref={panelRef} className="relative">
      <button
        type="button"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setIsOpen((current) => !current);
        }}
        className="flex w-full items-center justify-between gap-3 rounded-lg border border-border bg-background px-3 py-2 text-left text-sm transition-colors hover:bg-muted/50"
      >
        <div className="min-w-0">
          <div className="font-medium text-foreground">{currentSummary}</div>
          <div className="truncate text-[11px] font-mono text-muted-foreground">
            {currentValue}
          </div>
        </div>
        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
      </button>

      {isOpen ? (
        <div className="absolute left-0 top-full z-30 mt-2 w-[min(44rem,calc(100vw-2rem))] rounded-xl border border-border bg-card p-4 shadow-xl">
          <div className="mb-3 flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-semibold">Box Shadow</div>
              <div className="text-xs text-muted-foreground">
                Build multiple layers with x, y, blur, spread, color, and inset.
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => updateLayers([...layers, defaultBoxShadowLayer()])}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add layer
            </Button>
          </div>

          {layers.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border/70 px-4 py-5 text-sm text-muted-foreground">
              No shadow layers yet. Add a layer to begin.
            </div>
          ) : (
            <div className="space-y-3">
              {layers.map((layer, index) => (
                <div
                  key={`box-shadow-layer-${index}`}
                  className="rounded-lg border border-border/60 bg-background p-3"
                >
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <div className="text-xs font-medium text-muted-foreground">
                      Layer {index + 1}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          updateLayers(layers.filter((_, layerIndex) => layerIndex !== index))
                        }
                        className="h-7 w-7 p-0"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-6">
                    <label className="space-y-1">
                      <div className="text-[11px] font-medium text-muted-foreground">X</div>
                      <Input
                        type="number"
                        value={Number.isFinite(layer.x) ? layer.x : 0}
                        onChange={(event) =>
                          updateLayer(index, {
                            x: event.target.value === "" ? 0 : Number(event.target.value),
                          })
                        }
                      />
                    </label>

                    <label className="space-y-1">
                      <div className="text-[11px] font-medium text-muted-foreground">Y</div>
                      <Input
                        type="number"
                        value={Number.isFinite(layer.y) ? layer.y : 0}
                        onChange={(event) =>
                          updateLayer(index, {
                            y: event.target.value === "" ? 0 : Number(event.target.value),
                          })
                        }
                      />
                    </label>

                    <label className="space-y-1">
                      <div className="text-[11px] font-medium text-muted-foreground">
                        Blur
                      </div>
                      <Input
                        type="number"
                        value={Number.isFinite(layer.blur) ? layer.blur : 0}
                        onChange={(event) =>
                          updateLayer(index, {
                            blur:
                              event.target.value === "" ? 0 : Number(event.target.value),
                          })
                        }
                      />
                    </label>

                    <label className="space-y-1">
                      <div className="text-[11px] font-medium text-muted-foreground">
                        Spread
                      </div>
                      <Input
                        type="number"
                        value={Number.isFinite(layer.spread) ? layer.spread : 0}
                        onChange={(event) =>
                          updateLayer(index, {
                            spread:
                              event.target.value === "" ? 0 : Number(event.target.value),
                          })
                        }
                      />
                    </label>

                    <label className="space-y-1 xl:col-span-2">
                      <div className="text-[11px] font-medium text-muted-foreground">
                        Color
                      </div>
                      <div className="flex items-center gap-2">
                        <div
                          className="h-8 w-8 shrink-0 rounded-md border border-border"
                          style={{
                            backgroundColor: layer.color || "#000000",
                          }}
                        />
                        <Input
                          value={layer.color}
                          onChange={(event) =>
                            updateLayer(index, {
                              color: event.target.value,
                            })
                          }
                          placeholder="rgba(0, 0, 0, 0.14)"
                        />
                      </div>
                    </label>
                  </div>

                  <label className="mt-3 flex items-center gap-2">
                    <Checkbox
                      checked={layer.inset}
                      onCheckedChange={(checked) =>
                        updateLayer(index, {
                          inset: Boolean(checked),
                        })
                      }
                    />
                    <span className="text-xs text-muted-foreground">Inset</span>
                  </label>
                </div>
              ))}
            </div>
          )}

          <div className="mt-3 rounded-lg border border-border/60 bg-muted/30 px-3 py-2">
            <div className="text-[11px] font-medium text-muted-foreground">Preview</div>
            <div className="mt-2 rounded-lg border border-border bg-background p-4">
              <div
                className="h-16 w-full rounded-md bg-background"
                style={{
                  boxShadow: currentValue,
                }}
              />
            </div>
          </div>

          <div className="mt-3 rounded-lg bg-muted/30 px-3 py-2">
            <div className="text-[11px] font-medium text-muted-foreground">CSS</div>
            <div className="mt-1 break-words font-mono text-[11px] text-foreground">
              box-shadow: {currentValue};
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const PromoCardAssetField: React.FC<{
  value: unknown;
  onChange: (value: unknown) => void;
}> = ({ value, onChange }) => {
  const imagePicker = useImagePicker(false);
  const assetId = typeof value === "string" ? value : "";

  const handleSelectImage = () => {
    imagePicker.pickImages((files) => {
      if (files.length > 0) {
        const file = files[0];
        onChange(file.assetId);
      }
    });
  };

  return (
    <div className="space-y-2">
      {!assetId ? (
        <div className="rounded-lg border border-dashed border-border px-3 py-4 text-xs text-muted-foreground">
          No image selected.
        </div>
      ) : null}

      <Button type="button" variant="outline" size="sm" onClick={handleSelectImage}>
        {assetId ? "Change image" : "Select image"}
      </Button>

      {assetId ? (
        <div className="text-[11px] text-muted-foreground break-all">
          Asset ID: {assetId}
        </div>
      ) : null}
    </div>
  );
};

const PromoCardHrefField: React.FC<{
  value: unknown;
  onChange: (value: unknown) => void;
}> = ({ value, onChange }) => {
  const pages = useAppSelector((state) => state.page?.items || []) as Page[];
  const href = typeof value === "string" ? value : "";
  const [selectedPageId, selectedPageSlug] = useMemo(() => {
    const match = pages.find((page) => {
      const pageUrl = `/${page.slug || ""}`;
      return href === pageUrl || href === page.slug || href.endsWith(`/${page.slug || ""}`);
    });

    return [match?.id || "", match?.slug || ""] as const;
  }, [href, pages]);

  return (
    <div className="space-y-3">
      <Input
        type="url"
        value={href}
        onChange={(e) => onChange(e.target.value)}
        placeholder="/collections/new-arrivals"
      />

      <Select
        value={selectedPageId}
        onValueChange={(pageId) => {
          const selectedPage = pages.find((page) => page.id === pageId);
          if (selectedPage) {
            onChange(`/${selectedPage.slug || ""}`);
          }
        }}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Pick a page" />
        </SelectTrigger>
        <SelectContent>
          {pages.map((page) => (
            <SelectItem key={page.id} value={page.id}>
              {page.name}
              {page.slug ? ` /${page.slug}` : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {selectedPageSlug ? (
        <div className="text-[11px] text-muted-foreground">
          Selected page: /{selectedPageSlug}
        </div>
      ) : null}
    </div>
  );
};

const renderObjectEditor = (
  type: ComponentConfig["type"],
  value: unknown,
  onChange: (value: unknown) => void,
  path = "",
): React.ReactNode => {
  if (Array.isArray(value)) {
    return (
      <div className="space-y-2">
        {value.map((item, index) => {
          const itemPath = path ? `${path}.${index}` : `${index}`;
          return (
            <div key={itemPath} className="rounded-lg border border-border p-3 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">
                  Item {index + 1}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const next = [...value];
                    next.splice(index, 1);
                    onChange(next);
                  }}
                  className="h-7 w-7 p-0"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
              {renderObjectEditor(type, item, (nextItem) => {
                const next = [...value];
                next[index] = nextItem;
                onChange(next);
              }, itemPath)}
            </div>
          );
        })}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            const next = [...value, getArrayItemDefault(type, path, value)];
            onChange(next);
          }}
          className="w-full"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add item
        </Button>
      </div>
    );
  }

  if (!isPlainObject(value)) {
    return renderScalarEditor(path.split(".").pop() || "", value, onChange);
  }

  const entries = Object.entries(value);
  if (entries.length === 0) {
    console.warn("[DEBUG-shopbyicons] Structured object has no editable keys:", {
      type,
      path,
      value,
    });
  }
  const numericFieldNames = new Set([
    "min",
    "max",
    "limit",
    "offset",
    "duration",
    "delay",
    "fontSize",
    "lineHeight",
    "letterSpacing",
    "marginTop",
    "marginBottom",
    "marginLeft",
    "marginRight",
    "paddingTop",
    "paddingBottom",
    "paddingLeft",
    "paddingRight",
    "width",
    "height",
    "minHeight",
    "maxWidth",
    "borderWidth",
    "desktop",
    "tablet",
    "mobile",
    "columns",
    "gap",
    "itemsPerRow",
    "maxCount",
    "maxCategories",
    "pageSize",
    "maxSelections",
    "rating",
  ]);

  return (
    <div className="space-y-3">
      {entries.map(([key, entryValue]) => {
        const fieldPath = path ? `${path}.${key}` : key;
        const fieldLabel =
          type === "promo-card-list" && key === "assetId"
            ? "Image"
            : type === "promo-card-list" && key === "href"
              ? "URL"
              : toTitle(key);
        const nextValue = (next: unknown) => {
          onChange({
            ...value,
            [key]: next,
          });
        };

        const isArrayField = Array.isArray(entryValue);
        const isObjectField = isPlainObject(entryValue);
        const isResponsiveScalarField = isResponsiveScalarObject(entryValue);
        const isPromoCardAssetField = type === "promo-card-list" && key === "assetId";
        const isPromoCardHrefField = type === "promo-card-list" && key === "href";
        const isCardStyleShadowField = type === "card-style-config" && key === "shadow";
        const isNestedGrid =
          type === "grid-config" &&
          (key === "columns" || key === "gap" || key === "itemsPerRow");
        const isNestedBadge =
          type === "navigation-menu" && key === "badge" && isPlainObject(entryValue);
        const selectOptions =
          key === "target"
            ? [
                { label: "Same tab", value: "_self" },
                { label: "New tab", value: "_blank" },
              ]
            : type === "navigation-menu" && key === "type"
              ? [
                  { label: "Link", value: "link" },
                  { label: "Category", value: "category" },
                  { label: "Collection", value: "collection" },
                  { label: "Page", value: "page" },
                  { label: "Custom", value: "custom" },
                ]
              : type === "media-gallery" && key === "mediaType"
                ? [
                    { label: "Image", value: "image" },
                    { label: "Video", value: "video" },
                  ]
                : type === "card-style-config" && key === "overflow"
                    ? [
                        { label: "Hidden", value: "hidden" },
                        { label: "Visible", value: "visible" },
                        { label: "Auto", value: "auto" },
                      ]
                : [];
        const scalarOptions = {
          forceTextarea:
            key === "description" ||
            key === "html" ||
            key === "css" ||
            key === "js" ||
            key === "content",
          forceUrl: key.toLowerCase().includes("url"),
          forceColor: key.toLowerCase().includes("color"),
          forceNumber:
            numericFieldNames.has(key) ||
            key.toLowerCase().includes("count"),
          forceSelect: selectOptions.length > 0,
          selectOptions,
        };

        if (isResponsiveScalarField) {
          return (
            <ResponsiveScalarObjectField
              key={fieldPath}
              label={fieldLabel}
              fieldKey={key}
              value={entryValue}
              onChange={nextValue}
              scalarOptions={scalarOptions}
            />
          );
        }

        return (
          <div key={fieldPath} className="space-y-1 rounded-lg border border-border/60 p-3">
            <div className="text-xs font-medium text-muted-foreground">
              {fieldLabel}
            </div>
            {isArrayField ? (
              renderObjectEditor(type, entryValue, nextValue, fieldPath)
            ) : isPromoCardAssetField ? (
              <PromoCardAssetField value={entryValue} onChange={nextValue} />
            ) : isPromoCardHrefField ? (
              <PromoCardHrefField value={entryValue} onChange={nextValue} />
            ) : isCardStyleShadowField ? (
              <BoxShadowEditor value={entryValue} onChange={nextValue} />
            ) : isNestedGrid ? (
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(entryValue as ObjectRecord).map(([gridKey, gridValue]) => (
                  <div key={`${fieldPath}.${gridKey}`} className="space-y-1">
                    <div className="text-[11px] text-muted-foreground">
                      {toTitle(gridKey)}
                    </div>
                    {renderScalarEditor(gridKey, gridValue, (next) =>
                      nextValue({
                        ...(entryValue as ObjectRecord),
                        [gridKey]: next,
                      }),
                    )}
                  </div>
                ))}
              </div>
            ) : isNestedBadge ? (
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(entryValue as ObjectRecord).map(([badgeKey, badgeValue]) => (
                  <div key={`${fieldPath}.${badgeKey}`} className="space-y-1">
                    <div className="text-[11px] text-muted-foreground">
                      {toTitle(badgeKey)}
                    </div>
                    {renderScalarEditor(badgeKey, badgeValue, (next) =>
                      nextValue({
                        ...(entryValue as ObjectRecord),
                        [badgeKey]: next,
                      }),
                    )}
                  </div>
                ))}
              </div>
            ) : isObjectField ? (
              <div className="pl-2 border-l border-border/50">
                {renderObjectEditor(type, entryValue, nextValue, fieldPath)}
              </div>
            ) : (
              <div>
                {renderScalarEditor(
                  key,
                  entryValue,
                  nextValue,
                  scalarOptions,
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const StructuredValueEditor: React.FC<StructuredValueEditorProps> = ({
  type,
  value,
  onChange,
}) => {
  const normalizedValue = useMemo(
    () => normalizeStructuredValue(type, value),
    [type, value],
  );

  useEffect(() => {
    console.log("[DEBUG-shopbyicons] StructuredValueEditor render:", {
      type,
      rawValueType: Array.isArray(value) ? "array" : typeof value,
      normalizedValueType: Array.isArray(normalizedValue)
        ? "array"
        : typeof normalizedValue,
      normalizedKeys:
        normalizedValue &&
        typeof normalizedValue === "object" &&
        !Array.isArray(normalizedValue)
          ? Object.keys(normalizedValue as ObjectRecord)
          : [],
    });
  }, [type, value, normalizedValue]);

  switch (type) {
    case "product-selector": {
      const structuredValue = normalizedValue as Record<string, any>;
      const filter = (structuredValue.filter || {}) as Record<string, any>;
      const dynamic = (structuredValue.dynamic || {}) as Record<string, any>;
      const priceRange = (filter.priceRange || {}) as Record<string, any>;

      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border border-border/60 px-3 py-2">
            <div className="space-y-0.5">
              <div className="text-xs font-medium text-muted-foreground">
                Dynamic source
              </div>
              <div className="text-[11px] text-muted-foreground">
                Turn this off to use the static card inputs below.
              </div>
            </div>
            <Checkbox
              checked={structuredValue.enabled !== false}
              onCheckedChange={(checked) =>
                onChange({
                  ...structuredValue,
                  enabled: Boolean(checked),
                })
              }
            />
          </div>

          {structuredValue.enabled === false ? (
            <div className="rounded-lg border border-dashed border-border/70 px-3 py-2 text-xs text-muted-foreground">
              Dynamic product source is disabled. The section will use static
              card inputs.
            </div>
          ) : null}

          <div className="space-y-1">
            <div className="text-xs font-medium text-muted-foreground">Mode</div>
            <Select
              value={structuredValue.mode || "filter"}
              onValueChange={(mode) =>
                onChange({
                  ...structuredValue,
                  mode,
                })
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="specific">Specific products</SelectItem>
                <SelectItem value="filter">Filter products</SelectItem>
                <SelectItem value="dynamic">Dynamic products</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {structuredValue.mode === "specific" ? (
            <ProductSelector
              value={structuredValue.productIds || []}
              onValueChange={(productIds) =>
                onChange({
                  ...structuredValue,
                  mode: "specific",
                  productIds,
                })
              }
            />
          ) : structuredValue.mode === "dynamic" ? (
            <div className="space-y-3 rounded-lg border border-border p-3">
              <div className="space-y-1">
                <div className="text-xs font-medium text-muted-foreground">
                  Dynamic type
                </div>
                <Select
                  value={dynamic.type || "related"}
                  onValueChange={(nextType) =>
                    onChange({
                      ...structuredValue,
                      dynamic: {
                        ...dynamic,
                        type: nextType,
                      },
                    })
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select dynamic source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="related">Related</SelectItem>
                    <SelectItem value="recommended">Recommended</SelectItem>
                    <SelectItem value="frequently_bought">Frequently bought</SelectItem>
                    <SelectItem value="recently_viewed">Recently viewed</SelectItem>
                    <SelectItem value="same_collection">Same collection</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground">Limit</span>
                  <Input
                    type="number"
                    value={dynamic.limit ?? ""}
                    onChange={(e) =>
                      onChange({
                        ...structuredValue,
                        dynamic: {
                          ...dynamic,
                          limit: e.target.value === "" ? undefined : Number(e.target.value),
                        },
                      })
                    }
                  />
                </label>
                <label className="flex items-center gap-2 pt-6">
                  <Checkbox
                    checked={Boolean(dynamic.excludeCurrent)}
                    onCheckedChange={(checked) =>
                      onChange({
                        ...structuredValue,
                        dynamic: {
                          ...dynamic,
                          excludeCurrent: Boolean(checked),
                        },
                      })
                    }
                  />
                  <span className="text-xs text-muted-foreground">Exclude current</span>
                </label>
              </div>
            </div>
          ) : (
            <div className="space-y-3 rounded-lg border border-border p-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground">Status</span>
                  <Select
                    value={filter.status || "active"}
                    onValueChange={(status) =>
                      onChange({
                        ...structuredValue,
                        filter: {
                          ...filter,
                          status,
                        },
                      })
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground">Availability</span>
                  <Select
                    value={filter.availability || "in_stock"}
                    onValueChange={(availability) =>
                      onChange({
                        ...structuredValue,
                        filter: {
                          ...filter,
                          availability,
                        },
                      })
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Availability" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in_stock">In stock</SelectItem>
                      <SelectItem value="out_of_stock">Out of stock</SelectItem>
                      <SelectItem value="all">All</SelectItem>
                    </SelectContent>
                  </Select>
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground">Sort by</span>
                  <Select
                    value={filter.sortBy || "created_at"}
                    onValueChange={(sortBy) =>
                      onChange({
                        ...structuredValue,
                        filter: {
                          ...filter,
                          sortBy,
                        },
                      })
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Sort by" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="title">Title</SelectItem>
                      <SelectItem value="price">Price</SelectItem>
                      <SelectItem value="created_at">Created</SelectItem>
                      <SelectItem value="updated_at">Updated</SelectItem>
                      <SelectItem value="best_selling">Best selling</SelectItem>
                      <SelectItem value="trending">Trending</SelectItem>
                    </SelectContent>
                  </Select>
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground">Order</span>
                  <Select
                    value={filter.sortOrder || "desc"}
                    onValueChange={(sortOrder) =>
                      onChange({
                        ...structuredValue,
                        filter: {
                          ...filter,
                          sortOrder,
                        },
                      })
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Sort order" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="asc">Ascending</SelectItem>
                      <SelectItem value="desc">Descending</SelectItem>
                    </SelectContent>
                  </Select>
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground">Limit</span>
                  <Input
                    type="number"
                    value={filter.limit ?? ""}
                    onChange={(e) =>
                      onChange({
                        ...structuredValue,
                        filter: {
                          ...filter,
                          limit: e.target.value === "" ? undefined : Number(e.target.value),
                        },
                      })
                    }
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground">Offset</span>
                  <Input
                    type="number"
                    value={filter.offset ?? ""}
                    onChange={(e) =>
                      onChange({
                        ...structuredValue,
                        filter: {
                          ...filter,
                          offset: e.target.value === "" ? undefined : Number(e.target.value),
                        },
                      })
                    }
                  />
                </label>
              </div>

              <div className="space-y-2 rounded-lg border border-border/70 p-3">
                <div className="text-xs font-medium text-muted-foreground">
                  Price range
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="space-y-1">
                    <span className="text-[11px] text-muted-foreground">Min</span>
                    <Input
                      type="number"
                      value={priceRange.min ?? ""}
                      onChange={(e) =>
                        onChange({
                          ...structuredValue,
                          filter: {
                            ...filter,
                            priceRange: {
                              ...priceRange,
                              min:
                                e.target.value === ""
                                  ? undefined
                                  : Number(e.target.value),
                            },
                          },
                        })
                      }
                    />
                  </label>
                  <label className="space-y-1">
                    <span className="text-[11px] text-muted-foreground">Max</span>
                    <Input
                      type="number"
                      value={priceRange.max ?? ""}
                      onChange={(e) =>
                        onChange({
                          ...structuredValue,
                          filter: {
                            ...filter,
                            priceRange: {
                              ...priceRange,
                              max:
                                e.target.value === ""
                                  ? undefined
                                  : Number(e.target.value),
                            },
                          },
                        })
                      }
                    />
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground">Tags</span>
                  <Textarea
                    value={Array.isArray(filter.tags) ? filter.tags.join(", ") : ""}
                    onChange={(e) =>
                      onChange({
                        ...structuredValue,
                        filter: {
                          ...filter,
                          tags: e.target.value
                            .split(",")
                            .map((item) => item.trim())
                            .filter(Boolean),
                        },
                      })
                    }
                    rows={2}
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground">
                    Collections
                  </span>
                  <Textarea
                    value={Array.isArray(filter.collections) ? filter.collections.join(", ") : ""}
                    onChange={(e) =>
                      onChange({
                        ...structuredValue,
                        filter: {
                          ...filter,
                          collections: e.target.value
                            .split(",")
                            .map((item) => item.trim())
                            .filter(Boolean),
                        },
                      })
                    }
                    rows={2}
                  />
                </label>
              </div>
            </div>
          )}
        </div>
      );
    }

    case "style-config":
      return (
        <div className="space-y-2">
          <div className="rounded-lg border border-border/60 px-3 py-2 text-xs text-muted-foreground">
            One shared style object for headings, cards, links, text, images,
            and buttons.
          </div>
          {renderObjectEditor(type, normalizedValue, onChange)}
        </div>
      );

    case "swiper-config":
      return (
        <div className="space-y-2">
          <div className="rounded-lg border border-border/60 px-3 py-2 text-xs text-muted-foreground">
            Responsive Swiper carousel settings for desktop, tablet, and mobile.
          </div>
          {renderObjectEditor(type, normalizedValue, onChange)}
        </div>
      );

    case "section-style-config":
      return (
        <div className="space-y-2">
          <div className="rounded-lg border border-border/60 px-3 py-2 text-xs text-muted-foreground">
            Shared styling for the section heading.
          </div>
          {renderObjectEditor(type, normalizedValue, onChange)}
        </div>
      );

    case "card-image-style-config":
      return (
        <div className="space-y-2">
          <div className="rounded-lg border border-border/60 px-3 py-2 text-xs text-muted-foreground">
            Shared styling for every card image.
          </div>
          {renderObjectEditor(type, normalizedValue, onChange)}
        </div>
      );

    case "card-title-style-config":
      return (
        <div className="space-y-2">
          <div className="rounded-lg border border-border/60 px-3 py-2 text-xs text-muted-foreground">
            Shared styling for every card title.
          </div>
          {renderObjectEditor(type, normalizedValue, onChange)}
        </div>
      );

    case "card-cta-style-config":
      return (
        <div className="space-y-2">
          <div className="rounded-lg border border-border/60 px-3 py-2 text-xs text-muted-foreground">
            Shared styling for every card CTA.
          </div>
          {renderObjectEditor(type, normalizedValue, onChange)}
        </div>
      );

    case "card-price-style-config":
      return (
        <div className="space-y-2">
          <div className="rounded-lg border border-border/60 px-3 py-2 text-xs text-muted-foreground">
            Shared styling for every card price.
          </div>
          {renderObjectEditor(type, normalizedValue, onChange)}
        </div>
      );

    case "card-style-config":
      return (
        <div className="space-y-2">
          <div className="rounded-lg border border-border/60 px-3 py-2 text-xs text-muted-foreground">
            Shared styling for every card container.
          </div>
          {renderObjectEditor(type, normalizedValue, onChange)}
        </div>
      );

    case "promo-card-list":
      return (
        <div className="space-y-2">
          <div className="rounded-lg border border-border/60 px-3 py-2 text-xs text-muted-foreground">
            Static content used when dynamic products are disabled.
          </div>
          {renderObjectEditor(type, normalizedValue, onChange)}
        </div>
      );

    case "collection-selector": {
      const structuredValue = normalizedValue as Record<string, any>;
      const filter = (structuredValue.filter || {}) as Record<string, any>;

      return (
        <div className="space-y-4">
          <div className="space-y-1">
            <div className="text-xs font-medium text-muted-foreground">Mode</div>
            <Select
              value={structuredValue.mode || "filter"}
              onValueChange={(mode) =>
                onChange({
                  ...structuredValue,
                  mode,
                })
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="specific">Specific collections</SelectItem>
                <SelectItem value="filter">Filter collections</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {structuredValue.mode === "specific" ? (
            <CollectionSelector
              value={structuredValue.collectionIds || []}
              onValueChange={(collectionIds) =>
                onChange({
                  ...structuredValue,
                  mode: "specific",
                  collectionIds,
                })
              }
            />
          ) : (
            <div className="space-y-3 rounded-lg border border-border p-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground">Featured</span>
                  <Checkbox
                    checked={Boolean(filter.featured)}
                    onCheckedChange={(checked) =>
                      onChange({
                        ...structuredValue,
                        filter: {
                          ...filter,
                          featured: Boolean(checked),
                        },
                      })
                    }
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground">Status</span>
                  <Select
                    value={filter.status || "active"}
                    onValueChange={(status) =>
                      onChange({
                        ...structuredValue,
                        filter: {
                          ...filter,
                          status,
                        },
                      })
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                    </SelectContent>
                  </Select>
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground">Sort by</span>
                  <Select
                    value={filter.sortBy || "created_at"}
                    onValueChange={(sortBy) =>
                      onChange({
                        ...structuredValue,
                        filter: {
                          ...filter,
                          sortBy,
                        },
                      })
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Sort by" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="name">Name</SelectItem>
                      <SelectItem value="created_at">Created</SelectItem>
                      <SelectItem value="product_count">Product count</SelectItem>
                    </SelectContent>
                  </Select>
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground">Order</span>
                  <Select
                    value={filter.sortOrder || "desc"}
                    onValueChange={(sortOrder) =>
                      onChange({
                        ...structuredValue,
                        filter: {
                          ...filter,
                          sortOrder,
                        },
                      })
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Sort order" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="asc">Ascending</SelectItem>
                      <SelectItem value="desc">Descending</SelectItem>
                    </SelectContent>
                  </Select>
                </label>
              </div>

              <label className="space-y-1">
                <span className="text-xs font-medium text-muted-foreground">Limit</span>
                <Input
                  type="number"
                  value={filter.limit ?? ""}
                  onChange={(e) =>
                    onChange({
                      ...structuredValue,
                      filter: {
                        ...filter,
                        limit: e.target.value === "" ? undefined : Number(e.target.value),
                      },
                    })
                  }
                />
              </label>
            </div>
          )}
        </div>
      );
    }

    case "product-metafield-binding":
    case "collection-metafield-binding": {
      const structuredValue = normalizedValue as Record<string, any>;
      const appliedTo =
        type === "product-metafield-binding" ? "products" : "collections";

      return (
        <MetafieldSelector
          appliedTo={appliedTo}
          value={structuredValue as any}
          onValueChange={(nextValue) => onChange(nextValue)}
          helperText="Choose a configured metafield from settings."
        />
      );
    }

    case "category-selector": {
      const structuredValue = normalizedValue as Record<string, any>;
      const filter = (structuredValue.filter || {}) as Record<string, any>;

      return (
        <div className="space-y-4">
          <div className="space-y-1">
            <div className="text-xs font-medium text-muted-foreground">Mode</div>
            <Select
              value={structuredValue.mode || "all"}
              onValueChange={(mode) =>
                onChange({
                  ...structuredValue,
                  mode,
                })
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="specific">Specific categories</SelectItem>
                <SelectItem value="filter">Filter categories</SelectItem>
                <SelectItem value="all">All categories</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {structuredValue.mode === "specific" ? (
            <CategorySelector
              value={structuredValue.categoryIds || []}
              onValueChange={(categoryIds) =>
                onChange({
                  ...structuredValue,
                  mode: "specific",
                  categoryIds,
                })
              }
            />
          ) : (
            <div className="space-y-3 rounded-lg border border-border p-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground">
                    Level
                  </span>
                  <Input
                    type="number"
                    value={filter.level ?? ""}
                    onChange={(e) =>
                      onChange({
                        ...structuredValue,
                        filter: {
                          ...filter,
                          level:
                            e.target.value === "" ? undefined : Number(e.target.value),
                        },
                      })
                    }
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground">
                    Min products
                  </span>
                  <Input
                    type="number"
                    value={filter.minProducts ?? ""}
                    onChange={(e) =>
                      onChange({
                        ...structuredValue,
                        filter: {
                          ...filter,
                          minProducts:
                            e.target.value === "" ? undefined : Number(e.target.value),
                        },
                      })
                    }
                  />
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground">
                    Sort by
                  </span>
                  <Select
                    value={filter.sortBy || "position"}
                    onValueChange={(sortBy) =>
                      onChange({
                        ...structuredValue,
                        filter: {
                          ...filter,
                          sortBy,
                        },
                      })
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Sort by" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="name">Name</SelectItem>
                      <SelectItem value="product_count">Product count</SelectItem>
                      <SelectItem value="position">Position</SelectItem>
                    </SelectContent>
                  </Select>
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground">
                    Order
                  </span>
                  <Select
                    value={filter.sortOrder || "asc"}
                    onValueChange={(sortOrder) =>
                      onChange({
                        ...structuredValue,
                        filter: {
                          ...filter,
                          sortOrder,
                        },
                      })
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Sort order" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="asc">Ascending</SelectItem>
                      <SelectItem value="desc">Descending</SelectItem>
                    </SelectContent>
                  </Select>
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground">
                    Max categories
                  </span>
                  <Input
                    type="number"
                    value={filter.maxCategories ?? ""}
                    onChange={(e) =>
                      onChange({
                        ...structuredValue,
                        filter: {
                          ...filter,
                          maxCategories:
                            e.target.value === "" ? undefined : Number(e.target.value),
                        },
                      })
                    }
                  />
                </label>
                <label className="flex items-center gap-2 pt-6">
                  <Checkbox
                    checked={Boolean(filter.excludeEmpty)}
                    onCheckedChange={(checked) =>
                      onChange({
                        ...structuredValue,
                        filter: {
                          ...filter,
                          excludeEmpty: Boolean(checked),
                        },
                      })
                    }
                  />
                  <span className="text-xs text-muted-foreground">Exclude empty</span>
                </label>
              </div>
            </div>
          )}
        </div>
      );
    }

    default:
      console.error("[DEBUG-shopbyicons] StructuredValueEditor fallback branch hit:", {
        type,
        normalizedValue,
        rawValue: value,
      });
      return (
        <div className="space-y-3">
          {renderObjectEditor(type, normalizedValue, onChange)}
        </div>
      );
  }
};

export { StructuredValueEditor, defaultStructuredValue, normalizeStructuredValue };
